import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  buildStoreShippingTypeUpdatePayload,
  type StoreShippingFormOption,
} from '../helpers/storeCheckoutShippingTypeUpdate.utils';
import {
  resolveStoreCheckoutOrderIdFromUrl,
  isStoreCheckoutEditPage,
} from '../helpers/storeCheckoutStoreDetails.utils';
import { useStyleClass } from '@/hooks/useStyleClass';
import { Text } from '@/components/Text/Text';
import { useUpdateStoreMutation } from '@/api/StoreQuery';
import {
  STORE_FLOW_SELECTION_PINK,
  STORE_FLOW_SELECTION_PINK_ICON_FILL,
  STORE_FLOW_SELECTION_PURPLE,
  STORE_FLOW_SELECTION_PURPLE_ICON_FILL,
} from '@/constants/StoreFlow';
import './SelectShippingOption.scss';

interface ISelectShippingOptionProps {
  hasMtoItems?: boolean;
  onUserShippingOptionChange?: (
    nextShippingOption: StoreShippingFormOption,
    previousShippingOption?: StoreShippingFormOption
  ) => void;
}

const getShippingOptionTitleColor = (isSelected: boolean, optionValue: string): string => {
  if (!isSelected) {
    return '#474B6D';
  }

  if (optionValue === 'one-place') {
    return STORE_FLOW_SELECTION_PURPLE;
  }

  return STORE_FLOW_SELECTION_PINK;
};

const SelectShippingOption: React.FC<ISelectShippingOptionProps> = ({
  hasMtoItems = false,
  onUserShippingOptionChange,
}) => {
  const styleClass = useStyleClass('store-review-order-step', true); // Reusing styles from StoreReviewOrderStep
  const { watch, setValue } = useFormContext();
  const [updateStore] = useUpdateStoreMutation();
  const storeIdFromUrl = useMemo(() => resolveStoreCheckoutOrderIdFromUrl(), []);

  const isEditPage = isStoreCheckoutEditPage();

  const selectedShippingOption = watch('shippingOption') as StoreShippingFormOption | undefined;

  const isOptionDisabled = useCallback(
    (optionValue: StoreShippingFormOption) => {
      if (isEditPage) {
        return true;
      }

      if (hasMtoItems && optionValue === 'direct') {
        return true;
      }

      return false;
    },
    [hasMtoItems, isEditPage]
  );

  const handleShippingOptionSelect = useCallback(
    (nextShippingOption: StoreShippingFormOption) => {
      if (isOptionDisabled(nextShippingOption)) {
        return;
      }

      setValue('shippingOption', nextShippingOption);
      onUserShippingOptionChange?.(nextShippingOption, selectedShippingOption);

      if (!storeIdFromUrl || nextShippingOption === selectedShippingOption) {
        return;
      }

      updateStore(buildStoreShippingTypeUpdatePayload(Number(storeIdFromUrl), nextShippingOption))
        .unwrap()
        .catch(() => {
          /* shipping type save is best-effort when store exists */
        });
    },
    [
      isOptionDisabled,
      onUserShippingOptionChange,
      selectedShippingOption,
      setValue,
      storeIdFromUrl,
      updateStore,
    ]
  );

  useEffect(() => {
    if (!hasMtoItems || selectedShippingOption !== 'direct') {
      return;
    }

    setValue('shippingOption', 'one-place');

    if (!storeIdFromUrl) {
      return;
    }

    updateStore(buildStoreShippingTypeUpdatePayload(Number(storeIdFromUrl), 'one-place'))
      .unwrap()
      .catch(() => {
        /* shipping type save is best-effort when store exists */
      });
  }, [hasMtoItems, selectedShippingOption, setValue, storeIdFromUrl, updateStore]);

  const shippingOptions = [
    {
      value: 'one-place',
      title: 'Ship Everything to One Place',
      description: 'All ordered items will be delivered to one address.',
      icon: '/src/assets/svg/map.svg', // Using map icon as placeholder, can be replaced with specific icon
    },
    {
      value: 'direct',
      title: 'Ship Everything Directly to Whoever Orders',
      description: 'Customers will select and pay for shipping when they checkout.',
      icon: '/src/assets/svg/map.svg', // Using map icon as placeholder, can be replaced with specific icon
    },
  ];

  return (
    <div className={`${styleClass}__pricing-models store-shipping-options`}>
      {shippingOptions.map((option) => {
        const isDisabled = isOptionDisabled(option.value as StoreShippingFormOption);

        return (
          <div
            key={option.value}
            className={`${styleClass}__pricing-model-card ${
              selectedShippingOption === option.value
                ? `${styleClass}__pricing-model-card--selected`
                : ''
            } ${
              selectedShippingOption === option.value && option.value === 'one-place'
                ? `${styleClass}__pricing-model-card--selected-one-place`
                : ''
            } ${
              selectedShippingOption === option.value && option.value === 'direct'
                ? `${styleClass}__pricing-model-card--selected-direct`
                : ''
            } ${
              option.value === 'one-place'
                ? `${styleClass}__pricing-model-card--one-place-hover`
                : ''
            } ${
              option.value === 'direct' ? `${styleClass}__pricing-model-card--direct-hover` : ''
            } ${isDisabled ? `${styleClass}__pricing-model-card--disabled` : ''}`}
            onClick={() => handleShippingOptionSelect(option.value as StoreShippingFormOption)}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-disabled={isDisabled}
            onKeyDown={(e) => {
              if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleShippingOptionSelect(option.value as StoreShippingFormOption);
              }
            }}
            style={isDisabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
          >
            <div className={`${styleClass}__pricing-model-header`}>
              <div className={`${styleClass}__pricing-model-icon-column`}>
                {option.value === 'one-place' && (
                  <div className={`${styleClass}__pricing-model-icon-custom`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="80"
                      height="80"
                      viewBox="0 0 80 80"
                      fill="none"
                    >
                      <path
                        d="M40 80C62.0914 80 80 62.0914 80 40C80 17.9086 62.0914 0 40 0C17.9086 0 0 17.9086 0 40C0 62.0914 17.9086 80 40 80Z"
                        fill={STORE_FLOW_SELECTION_PURPLE_ICON_FILL}
                      />
                      <path
                        d="M56.0477 75.3276C56.0477 75.3276 56.5923 53.3246 16.7656 72.8753L56.0477 75.3276Z"
                        fill={STORE_FLOW_SELECTION_PURPLE_ICON_FILL}
                      />
                      <path
                        d="M56.0477 75.3276C56.0477 75.3276 56.5923 53.3246 16.7656 72.8753"
                        stroke={STORE_FLOW_SELECTION_PURPLE}
                        strokeDasharray="1 1"
                      />
                      <path
                        d="M48.0782 48.7598C48.0782 48.7598 32.8545 53.288 77.1199 56.2693L48.0782 48.7598Z"
                        fill={STORE_FLOW_SELECTION_PURPLE_ICON_FILL}
                      />
                      <path
                        d="M48.0782 48.7598C48.0782 48.7598 32.8545 53.288 77.1199 56.2693"
                        stroke={STORE_FLOW_SELECTION_PURPLE}
                        strokeDasharray="1 1"
                      />
                      <path
                        d="M52.0104 37.1497C52.0104 46.1395 40.4525 53.8451 40.4525 53.8451C40.4525 53.8451 28.8945 46.1395 28.8945 37.1497C28.8945 34.0844 30.1122 31.1446 32.2798 28.977C34.4473 26.8095 37.3871 25.5918 40.4525 25.5918C43.5178 25.5918 46.4576 26.8095 48.6252 28.977C50.7927 31.1446 52.0104 34.0844 52.0104 37.1497Z"
                        fill={STORE_FLOW_SELECTION_PURPLE_ICON_FILL}
                        stroke={STORE_FLOW_SELECTION_PURPLE}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M40.4539 41.0015C42.5814 41.0015 44.3062 39.2768 44.3062 37.1492C44.3062 35.0216 42.5814 33.2969 40.4539 33.2969C38.3263 33.2969 36.6016 35.0216 36.6016 37.1492C36.6016 39.2768 38.3263 41.0015 40.4539 41.0015Z"
                        fill={STORE_FLOW_SELECTION_PURPLE_ICON_FILL}
                        stroke={STORE_FLOW_SELECTION_PURPLE}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
                {option.value === 'direct' && (
                  <div className={`${styleClass}__pricing-model-icon-custom`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="80"
                      height="80"
                      viewBox="0 0 80 80"
                      fill="none"
                    >
                      <path
                        d="M40 80C62.0914 80 80 62.0914 80 40C80 17.9086 62.0914 0 40 0C17.9086 0 0 17.9086 0 40C0 62.0914 17.9086 80 40 80Z"
                        fill={STORE_FLOW_SELECTION_PINK_ICON_FILL}
                      />
                      <path
                        d="M48.0706 46.9655C48.0706 53.3562 39.8531 58.8342 39.8531 58.8342C39.8531 58.8342 31.6367 53.3562 31.6367 46.9655C31.6609 44.8021 32.5372 42.7356 34.0756 41.2143C35.6139 39.6931 37.6901 38.8398 39.8536 38.8398C42.0171 38.8398 44.0934 39.6931 45.6317 41.2143C47.17 42.7356 48.0464 44.8021 48.0706 46.9655Z"
                        stroke={STORE_FLOW_SELECTION_PINK}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M39.8528 49.7036C41.3657 49.7036 42.5923 48.4771 42.5923 46.9641C42.5923 45.4511 41.3657 44.2246 39.8528 44.2246C38.3398 44.2246 37.1133 45.4511 37.1133 46.9641C37.1133 48.4771 38.3398 49.7036 39.8528 49.7036Z"
                        stroke={STORE_FLOW_SELECTION_PINK}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M73.3332 33.6825C71.8956 39.9095 62.6565 43.3985 62.6565 43.3985C62.6565 43.3985 55.883 36.2127 57.3206 29.9857C57.8308 27.8833 59.1495 26.0668 60.9907 24.9306C62.8318 23.7944 65.0467 23.4301 67.1548 23.9168C69.2628 24.4034 71.0939 25.7018 72.2506 27.5302C73.4073 29.3585 73.7963 31.5692 73.3332 33.6825Z"
                        stroke={STORE_FLOW_SELECTION_PINK}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M64.7102 34.5056C66.1844 34.8459 67.6554 33.9268 67.9957 32.4526C68.3361 30.9784 67.4169 29.5074 65.9427 29.167C64.4685 28.8267 62.9975 29.7459 62.6572 31.2201C62.3168 32.6943 63.236 34.1652 64.7102 34.5056Z"
                        stroke={STORE_FLOW_SELECTION_PINK}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M24.5574 18.825C25.7768 25.0983 18.7556 32.0436 18.7556 32.0436C18.7556 32.0436 9.64486 28.2341 8.42544 21.9607C8.03638 19.8325 8.50231 17.6367 9.72213 15.8499C10.9419 14.063 12.8172 12.8293 14.941 12.4165C17.0647 12.0037 19.2656 12.4451 21.0659 13.6448C22.8663 14.8446 24.1208 16.706 24.5574 18.825Z"
                        stroke={STORE_FLOW_SELECTION_PINK}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17.0158 23.0822C18.501 22.7935 19.471 21.3555 19.1823 19.8703C18.8936 18.3851 17.4556 17.4152 15.9704 17.7038C14.4852 17.9925 13.5153 19.4305 13.804 20.9157C14.0927 22.4009 15.5307 23.3708 17.0158 23.0822Z"
                        stroke={STORE_FLOW_SELECTION_PINK}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.7227 33.3164C18.7227 33.3164 24.3124 45.2528 31.9596 42.9"
                        stroke={STORE_FLOW_SELECTION_PINK}
                        strokeDasharray="1 1"
                      />
                      <path
                        d="M60.7174 43.998C60.7174 43.998 55.1276 55.9345 47.4805 53.5817"
                        stroke={STORE_FLOW_SELECTION_PINK}
                        strokeDasharray="1 1"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <div className={`${styleClass}__pricing-model-title-column`}>
                <div className={`${styleClass}__pricing-model-title-container`}>
                  <Text
                    as="div"
                    size={16}
                    weight="bold"
                    color={getShippingOptionTitleColor(
                      selectedShippingOption === option.value,
                      option.value
                    )}
                    className={`${styleClass}__pricing-model-title`}
                  >
                    {option.value === 'direct'
                      ? 'Ship Everything Directly to Whoever'
                      : option.title}
                  </Text>
                  {option.value === 'direct' && (
                    <Text
                      as="div"
                      size={16}
                      weight="bold"
                      color={
                        selectedShippingOption === option.value
                          ? STORE_FLOW_SELECTION_PINK
                          : '#474B6D'
                      }
                      className={`${styleClass}__pricing-model-subtitle`}
                    >
                      Orders
                    </Text>
                  )}
                </div>
                <Text
                  as="div"
                  size={14}
                  color="#8D90AA"
                  className={`${styleClass}__pricing-model-description`}
                >
                  {option.description}
                </Text>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

SelectShippingOption.displayName = 'SelectShippingOption';

export default SelectShippingOption;
